/**
 * Poll integration sync status after a manual sync is triggered.
 *
 * When a user manually syncs an integration, this hook polls the API to detect when
 * the sync completes. It handles both static connections (ClassLink/Clever) and
 * dynamic connections differently:
 *
 * - Static connections: Polls the provider's last run endpoint
 * - Dynamic connections: Polls the connection list and finds the specific connection
 *
 * The hook automatically stops polling when:
 * 1. The sync status changes (sync completed)
 * 2. Maximum polling duration is reached (90 seconds)
 * 3. An error occurs
 * 4. The component unmounts
 *
 * @param {string} lastRunDate - The last run date to compare against (triggers polling)
 * @param {string|number} lastRunId - Connection ID to monitor (for dynamic connections)
 * @param {string} organizationCode - Organization code for API calls
 * @param {boolean} isStaticConnection - Whether this is a static connection
 * @param {Object} providerDetails - Provider details (for static connections)
 * @param {Function} onStatusChange - Callback invoked when status changes (status: "Success" | "Failed" | "error")
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useContainer } from "@src/contexts/container-context";
import { getConnectionProviderLastRunQueryKey } from "@queries/connection";

const POLL_INTERVAL = 5000; // Check every 5 seconds
const MAX_POLL_DURATION = 90000; // Stop after 90 seconds to prevent infinite polling

export function useSyncStatusPolling(
  lastRunDate,
  lastRunId,
  organizationCode,
  isStaticConnection,
  providerDetails,
  onStatusChange
) {
  const intervalRef = useRef();
  const timeoutRef = useRef();
  const queryClient = useQueryClient();
  const { integrationService } = useContainer();

  /**
   * Stops polling and cleans up intervals/timeouts.
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  /**
   * Checks the current sync status by comparing the last run date.
   * If the date has changed, the sync has completed and we notify the parent.
   *
   * Handles two different connection types:
   * - Static connections: Uses provider-specific last run endpoint
   * - Dynamic connections: Fetches connection list and finds the specific connection
   */
  const checkLastSync = useCallback(async () => {
    try {
      let last;
      if (isStaticConnection) {
        // For static connections (ClassLink/Clever), use provider-specific endpoint
        // Use React Query's fetchQuery to leverage caching and shared query key logic
        const queryKey = getConnectionProviderLastRunQueryKey(
          providerDetails?.providerName,
          organizationCode
        );
        const lastRunMapping = await queryClient.fetchQuery({
          queryKey,
          queryFn: () =>
            integrationService.getConnectionProviderLastRun(
              providerDetails?.providerName,
              [organizationCode]
            ),
        });
        last = lastRunMapping?.[organizationCode] ?? null;
      } else {
        // For dynamic connections, fetch the connection list and find our connection
        const list = await integrationService.getConnectionsList(
          organizationCode
        );
        const refreshedProvider = list.find(
          (connection) => connection.id === lastRunId
        );
        last = refreshedProvider
          ? {
              executionDateTime: refreshedProvider.lastRunDate,
              status: refreshedProvider.lastRunStatus,
            }
          : null;
      }

      // If the execution date changed, the sync completed
      if (last?.executionDateTime !== lastRunDate) {
        onStatusChange(last?.status);
        stopPolling();
      }
    } catch (error) {
      console.error("[Integrations] Error checking last sync:", error);
      onStatusChange("error");
      stopPolling();
    }
  }, [
    lastRunDate,
    lastRunId,
    organizationCode,
    isStaticConnection,
    providerDetails?.providerName,
    onStatusChange,
    stopPolling,
    queryClient,
    integrationService,
  ]);

  /**
   * Starts polling for sync status changes.
   * Sets up an interval to check status and a timeout to stop after max duration.
   */
  const startPolling = useCallback(() => {
    // Prevent multiple polling instances
    if (intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(checkLastSync, POLL_INTERVAL);

    // Safety timeout: stop polling after max duration
    timeoutRef.current = setTimeout(() => {
      stopPolling();
    }, MAX_POLL_DURATION);
  }, [checkLastSync, stopPolling]);

  useEffect(() => {
    if (lastRunDate && lastRunId) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [lastRunDate, lastRunId, startPolling, stopPolling]);

  return { startPolling, stopPolling };
}
